<script setup>
/**
 * Self-contained full-screen photo lightbox for the admin. (The public site has
 * its own Nuxt `<PhotoViewer>`; this is the no-Nuxt-deps equivalent used by the
 * gallery editor and — via PhotoViewerStub — the live-canvas preview.) Driven by
 * a v-model holding the open image index (null = closed). Esc / ← / → navigation,
 * prev/next, a counter and caption, body-scroll lock, teleported above the app.
 * Images are `{ url, alt_text?, caption? }`.
 */
import { computed, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  images: { type: Array, required: true },
  modelValue: { default: null },   // index of the open image, or null when closed
});
const emit = defineEmits(["update:modelValue"]);

const isOpen = computed(() => props.modelValue !== null && props.modelValue !== undefined);
const current = computed(() => (isOpen.value ? props.images[props.modelValue] ?? null : null));
const hasMany = computed(() => props.images.length > 1);

function close() { emit("update:modelValue", null); }
function go(delta) {
  if (!isOpen.value || props.images.length === 0) return;
  const n = props.images.length;
  emit("update:modelValue", (props.modelValue + delta + n) % n);
}

function onKeydown(e) {
  if (!isOpen.value) return;
  if (e.key === "Escape") close();
  else if (e.key === "ArrowRight") go(1);
  else if (e.key === "ArrowLeft") go(-1);
}

watch(isOpen, (open) => { document.body.style.overflow = open ? "hidden" : ""; });
onMounted(() => window.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  document.body.style.overflow = "";
});
</script>

<template>
  <Teleport to="body">
    <Transition name="pv">
      <div
        v-if="isOpen && current"
        class="fixed inset-0 z-[120] flex flex-col bg-slate-900/95 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        @click.self="close"
      >
        <!-- Top bar: counter + close -->
        <div class="flex items-center justify-between px-4 py-4 text-white/90 md:px-6">
          <span v-if="hasMany" class="text-sm tabular-nums tracking-wide text-white/70">{{ (modelValue ?? 0) + 1 }} / {{ images.length }}</span>
          <span v-else />
          <button type="button" class="grid h-10 w-10 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white" aria-label="關閉" @click="close">
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" /></svg>
          </button>
        </div>

        <!-- Image stage -->
        <div class="relative flex min-h-0 flex-1 items-center justify-center px-4 md:px-16" @click.self="close">
          <button v-if="hasMany" type="button" class="absolute left-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:left-4" aria-label="上一張" @click="go(-1)">
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>

          <img :src="current.url" :alt="current.alt_text || ''" class="max-h-full max-w-full select-none rounded-lg object-contain shadow-2xl" draggable="false" />

          <button v-if="hasMany" type="button" class="absolute right-2 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:right-4" aria-label="下一張" @click="go(1)">
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>
        </div>

        <!-- Caption -->
        <div class="min-h-[3rem] px-6 py-4 text-center">
          <p v-if="current.caption" class="mx-auto max-w-2xl text-sm leading-relaxed text-white/85">{{ current.caption }}</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.pv-enter-active,
.pv-leave-active { transition: opacity 240ms ease; }
.pv-enter-from,
.pv-leave-to { opacity: 0; }
</style>
